/**
 * PublicTrackPage — public tracking by tracking number, no auth required.
 * Mirrors the public web tracker that the cargo-server smart-link page
 * (`/t/<trackingNumber>`) deep-links into.
 *
 * Three modes:
 *   - URL with `:trackingNumber` → fetch and show
 *   - URL bare /track            → show input form
 *   - Authenticated user         → still works (no auth header sent)
 */
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Hash, MapPin, Loader, AlertCircle, ArrowLeft, Package, Truck, User, Phone } from 'lucide-react';
import { useTrackShipment } from '@/hooks/useShipments';
import { Button } from '@/components/common/Button';
import { TextField } from '@/components/common/TextField';
import { StatusBadge } from '@/components/shipments/StatusBadge';

export default function PublicTrackPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { trackingNumber: paramTn } = useParams<{ trackingNumber: string }>();
  const [input, setInput] = useState('');
  const tn = paramTn || null;
  const { data: s, isLoading, isError } = useTrackShipment(tn);

  if (!tn) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="bg-surface border border-border rounded-2xl p-8 max-w-md w-full">
          <h1 className="text-xl font-bold text-primary mb-2 flex items-center gap-2">
            <MapPin size={22} /> {t('track_shipment', 'تتبّع شحنة')}
          </h1>
          <p className="text-text-secondary text-sm mb-5">
            {t('track_intro', 'أدخل رقم التتبّع (مثل CW-XXXXXX-XXXX) لمعرفة حالة الشحنة')}
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const v = input.trim().toUpperCase();
              if (v) navigate(`/track/${encodeURIComponent(v)}`);
            }}
            className="space-y-3"
          >
            <TextField
              dir="ltr"
              placeholder="CW-XXXXXX-XXXX"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              autoFocus
            />
            <Button type="submit" fullWidth>{t('track', 'تتبّع')}</Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background px-4 py-8">
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-text-secondary mb-4 hover:text-primary"
        >
          <ArrowLeft size={18} /> {t('back', 'رجوع')}
        </button>

        {isLoading ? (
          <div className="bg-surface border border-border rounded-2xl p-12 text-center">
            <Loader size={32} className="animate-spin mx-auto text-text-tertiary mb-2" />
            <p className="text-text-secondary">{t('loading', 'جاري التحميل...')}</p>
          </div>
        ) : isError || !s ? (
          <div className="bg-surface border border-border rounded-2xl p-8 text-center">
            <AlertCircle size={36} className="mx-auto text-error mb-3" />
            <p className="text-text-primary font-bold mb-1">
              {t('shipment_not_found', 'الشحنة غير موجودة')}
            </p>
            <p className="text-text-secondary text-sm mb-4" dir="ltr">{tn}</p>
            <Button onClick={() => navigate('/track')}>
              {t('try_another', 'جرّب رقماً آخر')}
            </Button>
          </div>
        ) : (
          <>
            <div className="bg-surface border border-border rounded-2xl p-6 mb-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 font-mono text-primary">
                  <Hash size={14} /> {s.tracking_number}
                </div>
                <StatusBadge status={s.status} size="md" />
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <Item icon={<MapPin size={14} />} label={t('from', 'من')} value={s.pickup_city || s.origin_city || '—'} />
                <Item icon={<MapPin size={14} className="text-secondary" />} label={t('to', 'إلى')} value={s.receiver_city || s.destination_city || '—'} />
                <Item icon={<Package size={14} />} label={t('weight', 'الوزن')} value={`${s.weight} كغ`} />
                {s.driver_name && (
                  <Item icon={<Truck size={14} />} label={t('driver', 'السائق')} value={s.driver_name} />
                )}
              </div>
            </div>

            {/* Receiver info — only contact details so any party can confirm */}
            <div className="bg-surface border border-border rounded-2xl p-6">
              <h3 className="font-bold text-text-primary mb-3">{t('parties', 'الأطراف')}</h3>
              <div className="space-y-3 text-sm">
                <Item icon={<User size={14} />} label={t('receiver', 'المستلِم')} value={s.receiver_name} />
                {s.receiver_phone && (
                  <Item icon={<Phone size={14} />} label={t('phone', 'الهاتف')} value={s.receiver_phone} ltr />
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Item({ icon, label, value, ltr }: { icon: React.ReactNode; label: string; value: React.ReactNode; ltr?: boolean }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-text-tertiary text-xs mb-0.5">{icon}{label}</div>
      <div className="text-text-primary font-medium" dir={ltr ? 'ltr' : undefined}>{value}</div>
    </div>
  );
}
